import { DataAction, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Injectable } from '@angular/core';
import { Selector, State, Store } from '@ngxs/store';
import { produce } from 'immer';
import { AppInsightsService } from 'src/app/services/app-insights/appinsights.service';
import { Utilities } from 'src/app/services/utilities';
import { environment } from 'src/environments/environment';
import { AppInitializerService, IActionConfig, IInterviewConfig, IInterviewConfiguration, IPermissionSettings, IWebAppApiConfigs } from './../app-initializer/app-initializer.service';
import { IConfigurationStateModel } from './configuration-state.models';

@StateRepository()
@State<IConfigurationStateModel>({
    name: 'configurationState',
    defaults: {
        extra: null,
        initialized: false,
    }
})
@Injectable()
export class ConfigurationService extends NgxsDataRepository<IConfigurationStateModel>{
    static readonly appVersion = environment.appVersion;

    baseUrl = Utilities.baseUrl();

    constructor(private store: Store, private appInsightsService: AppInsightsService) {
        super();
    }

    @Selector()
    static classificationInterviewSelector(state: IConfigurationStateModel) {
        return state.extra.classificationInterview;
    }

    @Selector()
    static documentInterviewApiBaseUrlSelector(state: IConfigurationStateModel) {
        return state.extra.documentInterviewApiBaseUrl;
    }

    @Selector()
    static kofaxCaptureBaseUrlSelector(state: IConfigurationStateModel) {
        return state.extra.kofaxCaptureBaseUrl;
    }

    @Selector()
    static instrumentationKeySelector(state: IConfigurationStateModel) {
        return state.extra.instrumentationKey;
    }

    @Selector()
    static webViewerPDFTronKeySelector(state: IConfigurationStateModel) {
        return state.extra.webViewerPDFTronKey;
    }

    @Selector()
    static webAppApiBaseUrlSelector(state: IConfigurationStateModel) {
        return state.extra.webAppApiBaseUrl;
    }

    @Selector()
    static interviewConfigSelector(state: IConfigurationStateModel) {
        return state.extra.interviews;
    }

    @Selector()
    static actionConfigSelector(state: IConfigurationStateModel) {
        return state.extra.actions;
    }

    @Selector()
    static initializedSelector(state: IConfigurationStateModel) {
        return state.initialized;
    }

    @Selector()
    static endpointConfigsSelector(state: IConfigurationStateModel) {
        return state.extra.webAppApi
    }

    @Selector()
    static memberSessionExpirationMinutesSelector(state: IConfigurationStateModel) {
        return state.extra.memberSessionExpirationMinutes
    }

    @Selector()
    static telephonyParametersExpirationMinutesSelector(state: IConfigurationStateModel) {
        return state.extra.telephonyParametersExpirationMinutes;
    }

    @Selector()
    static permissionSettingsSelector(state: IConfigurationStateModel) {
        return state.extra.permissionSettings;
    }

    @Selector()
    static googleServiceApiKeySelector(state: IConfigurationStateModel) {
        return state.extra.googleServiceApiKey;
    }

    @Selector()
    static searchDebounceTimeSelector(state: IConfigurationStateModel) {
        const debounceTime = state.extra.searchDebounceTime > 0 ? state.extra.searchDebounceTime : 2500;
        return debounceTime;
    }

    @Selector()
    static repositoryManagerServicePageSize(state: IConfigurationStateModel) {
        return state.extra.repositoryManagerServicePageSize > 0 ? state.extra.repositoryManagerServicePageSize : 25;
    }

    @Selector()
    static notificationLoadIntervalMinutesSelector(state: IConfigurationStateModel) {
        return state.extra.notificationLoadIntervalMinutes
    }

    getBaseUrl(requestCategory?: string, force?: ForceBaseUrl) {
        switch (true) {
            case force === 'WEBAPI':
                return (Utilities.removeEndChar(this.webAppApiBaseUrl, '/') + '/');
            case force === 'LEGACY':
                return this.baseUrl;
            default:
                return this.webApi[requestCategory] ? (Utilities.removeEndChar(this.webAppApiBaseUrl, '/') + '/') : this.baseUrl;
        }
    }

    get webAppApiBaseUrl() {
        return this.store.selectSnapshot<string>(ConfigurationService.webAppApiBaseUrlSelector);
    }

    get kofaxCaptureBaseUrl() {
        return this.store.selectSnapshot<string>(ConfigurationService.kofaxCaptureBaseUrlSelector);
    }

    get instrumentationKey() {
        return this.store.selectSnapshot<string>(ConfigurationService.instrumentationKeySelector);
    }

    get webViewerPDFTronKey() {
        return this.store.selectSnapshot<string>(ConfigurationService.webViewerPDFTronKeySelector);
    }

    get documentInterviewApiBaseUrl() {
        return this.store.selectSnapshot<string>(ConfigurationService.documentInterviewApiBaseUrlSelector);
    }

    get classificationInterview() {
        return this.store.selectSnapshot<IInterviewConfiguration>(ConfigurationService.classificationInterviewSelector);
    }

    get permissionSettings() {
        return this.store.selectSnapshot<IPermissionSettings>(ConfigurationService.permissionSettingsSelector);
    }

    get searchDebounceTime() {
        return this.store.selectSnapshot<number>(ConfigurationService.searchDebounceTimeSelector);
    }
    get repositoryManagerServicePageSize() {
        return this.store.selectSnapshot<number>(ConfigurationService.repositoryManagerServicePageSize);
    }
    get memberSessionExpirationMinutes() {
        try {
            const result = parseInt(this.store.selectSnapshot<string>(ConfigurationService.memberSessionExpirationMinutesSelector), 10);
            if (Number.isNaN(result) || result == null) {
                throw result;
            }

            return result;
        } catch {
            return 30;
        }
    }

    get notificationLoadIntervalMilliseconds() {
        try {
            const result = this.store.selectSnapshot<number>(ConfigurationService.notificationLoadIntervalMinutesSelector);
            if (Number.isNaN(result) || result == null || result == 0) {
                return 2400000;
            }

            return result * 1000;
        } catch {
            return 2400000;
        }
    }

    get telephonyParametersExpirationMinutes() {
        try {
            const result = parseInt(this.store.selectSnapshot<string>(ConfigurationService.telephonyParametersExpirationMinutesSelector), 10);
            if (Number.isNaN(result) || result == null) {
                throw result;
            }

            return result;
        } catch {
            return 30;
        }
    }

    get interviewConfig() {
        return (name?: string) => {
            if (name) {
                return this.store.selectSnapshot<IInterviewConfig[]>(ConfigurationService.interviewConfigSelector).find(x => x.packageName.toLowerCase() == name.toLowerCase());
            }
            else {
                return this.store.selectSnapshot<IInterviewConfig[]>(ConfigurationService.interviewConfigSelector);
            }
        }
    }

    get actionConfig() {
        return (name?: string) => {
            if (name) {
                return this.store.selectSnapshot<IActionConfig[]>(ConfigurationService.actionConfigSelector).find(x => x.packageName.toLowerCase() == name.toLowerCase());
            }
            else {
                return this.store.selectSnapshot<IActionConfig[]>(ConfigurationService.actionConfigSelector)[0];
            }
        }
    }

    get initialized() {
        return this.store.selectSnapshot<boolean>(ConfigurationService.initializedSelector);
    }

    get initializedAsync() {
        return this.store.select<boolean>(ConfigurationService.initializedSelector);
    }

    get webApi() {
        return this.store.selectSnapshot<IWebAppApiConfigs>(ConfigurationService.endpointConfigsSelector);
    }

    webAppApiBaseUrlFunc = () => this.store.selectSnapshot<string>(ConfigurationService.webAppApiBaseUrlSelector);
    kofaxCaptureBaseUrlFunc = () => this.store.selectSnapshot<string>(ConfigurationService.kofaxCaptureBaseUrlSelector);
    instrumentationKeyFunc = () => this.store.selectSnapshot<string>(ConfigurationService.instrumentationKeySelector);
    webViewerPDFTronKeyFunc = () => this.store.selectSnapshot<string>(ConfigurationService.webViewerPDFTronKeySelector);
    documentInterviewApiBaseUrlFunc = () => this.store.selectSnapshot<string>(ConfigurationService.documentInterviewApiBaseUrlSelector);
    interviewConfigFunc = () => this.store.selectSnapshot<IInterviewConfig[]>(ConfigurationService.interviewConfigSelector);
    classificationInterviewFunc = () => this.store.selectSnapshot<IInterviewConfiguration>(ConfigurationService.classificationInterviewSelector);
    searchDebounceTimeFunc = () => this.store.selectSnapshot<number>(ConfigurationService.searchDebounceTimeSelector);
    actionConfigFunc = () => this.store.selectSnapshot<IActionConfig[]>(ConfigurationService.actionConfigSelector);

    async ngxsAfterBootstrap() {
        super.ngxsAfterBootstrap();
        await this.setExtraConfiguration();
    }

    @DataAction()
    async setExtraConfiguration() {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IConfigurationStateModel) => {
            draft.initialized = false;
        }));

        const extraConfiguration = AppInitializerService.configuration;

        this.appInsightsService.setConfig(extraConfiguration.instrumentationKey, extraConfiguration.instrumentationClientName, extraConfiguration.appInsightsConfiguration);

        this.ctx.setState(produce(this.ctx.getState(), (draft: IConfigurationStateModel) => {
            draft.extra = extraConfiguration;
            draft.initialized = true;
        }));
    }
}

export declare type ForceBaseUrl = 'LEGACY' | 'WEBAPI' | undefined;
